import { z } from 'zod';
/**
 * Personalization profile — wire shape returned by
 * `GET /api/orgs/:o/personalization`. Drives the portal's nav order,
 * scorecard emphasis, playbook display priority, and the Today/
 * Command Center hero cards.
 *
 * Lives in `@leanopx/shared` per the project convention: types that
 * cross the network boundary live here as Zod schemas, and both apps
 * import via `workspace:*`. Previously duplicated in
 * `apps/web/src/lib/api.ts` and `apps/functions/src/synthesis/
 * personalization.ts`; closing that drift now.
 */
/**
 * Categorical kind of a briefing highlight. Drives the Today-page card's
 * icon + accent color.
 */
export const BriefingHighlightKind = z.enum([
    'north-star',
    'priority',
    'industry',
    'signal',
    'playbook',
    'note',
]);
/**
 * One hero card on the Today/Command Center page. Carries enough
 * structure for a rich card render (icon + body + optional severity +
 * optional drill-in link); thin label-only cards stay valid by leaving
 * `body`/`severity`/`link` undefined.
 */
export const BriefingHighlight = z.object({
    /** 4-8 word headline shown as the card title. */
    label: z.string().min(1).max(80),
    /** 1-2 sentence body explaining the why; optional but strongly preferred. */
    body: z.string().max(400).optional(),
    /** Drives the card icon + accent. */
    kind: BriefingHighlightKind,
    /** Optional severity ('info' / 'watch' / 'critical'). */
    severity: z.enum(['info', 'watch', 'critical']).optional(),
    /** Optional internal link (TanStack Router path) to drill into the source. */
    link: z.string().optional(),
});
/**
 * Per-measurable visibility/emphasis hint on the scorecard. Keys are
 * measurableId (e.g. `m-runway`, `m-nps`).
 */
export const ScorecardEmphasis = z.record(z.string(), z.enum(['primary', 'secondary', 'hidden']));
/**
 * Personalization profile. Synthesized server-side either by the
 * rules-based heuristic (`computePersonalizationProfile`) or the
 * LLM-driven path (`synthesizePersonalizationProfile`); cached for
 * 24h in `personalizationProfiles/{orgId}` keyed on a fingerprint of
 * the inputs.
 */
export const PersonalizationProfile = z.object({
    /** Surface IDs in display order (matches sidebar-nav.tsx route paths). */
    navOrder: z.array(z.string()),
    /** Per-measurable emphasis hint. */
    scorecardEmphasis: ScorecardEmphasis,
    /** Playbook IDs in display order (top-down on `/playbook`). */
    playbookPriority: z.array(z.string()),
    /** Hero cards for the Today/Command Center page (max 6). */
    briefingHighlights: z.array(BriefingHighlight).max(6),
    /**
     * ISO-8601 timestamp marking when the profile was synthesized. Used
     * by the Today surface to render a provenance line so the operator
     * knows the briefing was machine-generated and how fresh it is.
     * Optional for backward compatibility with cached docs from before
     * provenance shipped.
     */
    synthesizedAt: z.string().optional(),
    /**
     * Synthesis engine that produced the profile. Either a humanized
     * Vertex model name ('Gemini 2.5 Flash') or the literal 'rules-based'
     * when the deterministic fallback path ran. Optional for backward
     * compatibility.
     */
    model: z.string().optional(),
});
